import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { clsx } from 'clsx';
import Backdrop from 'components/Modal/Backdrop';
import dayjs from 'dayjs';

import {
  getClimateExposureLevel,
  isHighSeverityClimateFinding,
} from '../utils/monitoringResultsUtils';

import type {
  IClimateFinding,
  IClimateMonitoredEntity,
} from 'constants/interfaces/IMonitoringSignalResult';
import type IClimateAnalysisModal from './interfaces/IClimateAnalysisModal';

import styles from './sass/ClimateAnalysisModal.module.scss';

type TClimateDetailView = 'summary' | 'diagram';

const formatLocation = (
  location?: { country?: string, region?: string | null, locality?: string | null },
): string => {
  if (!location) {
    return '—';
  }

  return [location.locality, location.region, location.country]
    .filter((part): part is string => Boolean(part && String(part).trim()))
    .join(', ') || '—';
};

const getEntityFindings = (
  entity: IClimateMonitoredEntity,
  findings: IClimateFinding[],
): IClimateFinding[] => findings.filter((finding) => finding.entity_unit_id === entity.unitId);

const pickPrimaryClimateFinding = (
  entityFindings: IClimateFinding[],
): IClimateFinding | null => (
  entityFindings.find((finding) => finding.severity === 'critical')
  || entityFindings.find((finding) => finding.severity === 'high')
  || entityFindings[0]
  || null
);

const isEntityImpactEligible = (
  entityFindings: IClimateFinding[],
): boolean => entityFindings.some(isHighSeverityClimateFinding);

function ClimateAnalysisModal({
  open,
  results,
  themeFilter = 'all',
  onThemeFilterChange,
  onClose,
  showImpactScenarioCta = false,
  isImpactScenarioCreating = false,
  onCreateImpactScenario,
}: IClimateAnalysisModal): React.JSX.Element | null {
  const { t } = useTranslation();
  const allEntities = useMemo(() => results?.entities || [], [results]);
  const allFindings = useMemo(() => results?.findings || [], [results]);

  const themeOptions = useMemo(() => {
    const themes = Array.from(new Set(allFindings.map((finding) => finding.theme)));

    return ['all', ...themes];
  }, [allFindings]);

  const findings = useMemo(
    () => (
      themeFilter === 'all'
        ? allFindings
        : allFindings.filter((finding) => finding.theme === themeFilter)
    ),
    [allFindings, themeFilter],
  );

  const entities = useMemo(() => {
    if (themeFilter === 'all') {
      return allEntities;
    }

    const unitIds = new Set(findings.map((finding) => finding.entity_unit_id));

    return allEntities.filter((entity) => (
      unitIds.has(entity.unitId)
      || (entity.climate_profile.hazards_active || []).includes(themeFilter)
    ));
  }, [allEntities, findings, themeFilter]);

  const findingsById = useMemo(
    () => new Map(findings.map((finding) => [finding.id, finding])),
    [findings],
  );

  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [expandedEntityIds, setExpandedEntityIds] = useState<Set<number>>(new Set());
  const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);
  const [detailView, setDetailView] = useState<TClimateDetailView>('summary');

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    document.body.style.overflow = 'hidden';

    const firstEntity = entities[0];

    if (firstEntity) {
      setSelectedEntityId(firstEntity.unitId);
      setExpandedEntityIds(new Set([firstEntity.unitId]));

      const primaryFinding = pickPrimaryClimateFinding(getEntityFindings(firstEntity, findings));

      setSelectedFindingId(primaryFinding?.id ?? findings[0]?.id ?? null);
    } else {
      setSelectedEntityId(null);
      setSelectedFindingId(findings[0]?.id ?? null);
    }

    setDetailView('summary');

    return () => {
      document.body.style.overflow = '';
    };
  }, [open, themeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) {
    return null;
  }

  const selectEntity = (unitId: number) => {
    setSelectedEntityId(unitId);

    const entity = entities.find((candidate) => candidate.unitId === unitId);
    const entityFindings = entity ? getEntityFindings(entity, findings) : [];
    const primaryFinding = pickPrimaryClimateFinding(entityFindings);

    setSelectedFindingId(primaryFinding?.id ?? null);
    setDetailView('summary');
  };

  const toggleEntity = (unitId: number) => {
    selectEntity(unitId);
    setExpandedEntityIds((current) => {
      const next = new Set(current);

      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        next.add(unitId);
      }

      return next;
    });
  };

  const selectedFinding = findingsById.get(selectedFindingId || '') || null;
  const selectedEntity = entities.find((entity) => entity.unitId === selectedEntityId) || null;
  const selectedEntityFindings = selectedEntity
    ? getEntityFindings(selectedEntity, findings)
    : [];
  const canCreateImpact = Boolean(
    showImpactScenarioCta
    && onCreateImpactScenario
    && selectedEntity
    && isEntityImpactEligible(selectedEntityFindings),
  );

  const highRiskCount = findings.filter(isHighSeverityClimateFinding).length;

  const modal = (
    <Backdrop>
      <div className={styles.root} role="dialog" aria-modal="true">
        <div className={styles.header}>
          <h2 className={styles.title}>
            {t('valueChainMap.monitoring.results.climateAnalysisTitle')}
          </h2>
          <button
            type="button"
            className={clsx(styles.iconButton, styles.closeButton)}
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <CloseIcon aria-hidden="true" />
          </button>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.kpis}>
            <span className={styles.kpiChip}>
              {t('valueChainMap.monitoring.results.climateModalKpiEntities', {
                count: entities.length,
              })}
            </span>
            <span className={styles.kpiChip}>
              {t('valueChainMap.monitoring.results.climateModalKpiFindings', {
                count: findings.length,
              })}
            </span>
            <span className={styles.kpiChip}>
              {t('valueChainMap.monitoring.results.climateModalKpiHighRisk', {
                count: highRiskCount,
              })}
            </span>
          </div>
          <p className={styles.hint}>
            {t('valueChainMap.monitoring.results.climateModalHint')}
          </p>
          {themeOptions.length > 1 && onThemeFilterChange ? (
            <div className={styles.kpis}>
              {themeOptions.map((theme) => (
                <button
                  key={theme}
                  type="button"
                  className={clsx(styles.kpiChip, {
                    [styles.supplierRowSelected]: themeFilter === theme,
                  })}
                  onClick={() => onThemeFilterChange(theme)}
                >
                  {theme === 'all'
                    ? t('valueChainMap.monitoring.results.climateAllThemes')
                    : t(`valueChainMap.monitoring.results.climateTheme.${theme}`, {
                      defaultValue: theme,
                    })}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className={styles.layout}>
          <div className={styles.tablePane}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('valueChainMap.monitoring.results.climateModalKind')}</th>
                  <th>{t('valueChainMap.monitoring.results.climateModalLocation')}</th>
                  <th>{t('valueChainMap.monitoring.results.climateModalExposure')}</th>
                  <th>{t('valueChainMap.monitoring.results.climateModalTrend')}</th>
                  <th>{t('valueChainMap.monitoring.results.climateModalMaterials')}</th>
                </tr>
              </thead>
              <tbody>
                {entities.map((entity) => {
                  const entityFindings = getEntityFindings(entity, findings);
                  const isExpanded = expandedEntityIds.has(entity.unitId);
                  const isSelected = selectedEntityId === entity.unitId;
                  const scoreLevel = getClimateExposureLevel(entity.exposure_score);

                  return (
                    <React.Fragment key={entity.unitId}>
                      <tr
                        className={clsx(
                          styles.supplierRow,
                          styles.supplierRowClickable,
                          { [styles.supplierRowSelected]: isSelected },
                        )}
                        onClick={() => toggleEntity(entity.unitId)}
                      >
                        <td>
                          {t(`valueChainMap.monitoring.results.climateEntityKind.${entity.entity_kind}`, {
                            defaultValue: entity.entity_kind,
                          })}
                        </td>
                        <td>
                          <strong>{entity.name}</strong>
                          <div>{formatLocation(entity.location)}</div>
                        </td>
                        <td>
                          <span
                            className={clsx(styles.exposureBadge, {
                              [styles.exposure_high]: scoreLevel === 'high',
                              [styles.exposure_mid]: scoreLevel === 'mid',
                              [styles.exposure_low]: scoreLevel === 'low',
                            })}
                          >
                            {entity.exposure_score}
                          </span>
                        </td>
                        <td>{entity.trend || '—'}</td>
                        <td>
                          {(entity.related_materials || []).slice(0, 2).join(', ') || '—'}
                          {' '}
                          (
                          {t('valueChainMap.monitoring.results.climateModalIssueCount', {
                            count: entityFindings.length,
                          })}
                          )
                        </td>
                      </tr>
                      {isExpanded ? entityFindings.map((finding) => (
                        <tr
                          key={finding.id}
                          className={clsx(styles.issueRow, {
                            [styles.issueRowActive]: selectedFindingId === finding.id,
                          })}
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedEntityId(entity.unitId);
                            setSelectedFindingId(finding.id);
                            setDetailView('summary');
                          }}
                        >
                          <td colSpan={5}>
                            <div className={styles.issueRowInner}>
                              <div className={styles.issueCopy}>
                                <div className={styles.nodeTitle}>{finding.title}</div>
                                <div className={styles.nodeMeta}>
                                  {t(`valueChainMap.monitoring.results.climateTheme.${finding.theme}`, {
                                    defaultValue: finding.theme,
                                  })}
                                  {' · '}
                                  {finding.severity}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={styles.detailPane}>
            <div className={styles.detailHeader}>
              <h3 className={styles.detailTitle}>
                {selectedFinding?.title
                  || t('valueChainMap.monitoring.results.climateModalSelectIssue')}
              </h3>
              <div className={styles.detailToggle}>
                <button
                  type="button"
                  className={clsx(styles.detailViewButton, {
                    [styles.detailViewButtonActive]: detailView === 'summary',
                  })}
                  onClick={() => setDetailView('summary')}
                >
                  {t('valueChainMap.monitoring.results.climateModalSummaryTab')}
                </button>
                <button
                  type="button"
                  className={clsx(styles.detailViewButton, {
                    [styles.detailViewButtonActive]: detailView === 'diagram',
                  })}
                  onClick={() => setDetailView('diagram')}
                >
                  {t('valueChainMap.monitoring.results.climateModalDiagramTab')}
                </button>
              </div>
            </div>

            {selectedFinding ? (
              <>
                <div className={styles.detailMeta}>
                  {selectedFinding.entity_name}
                  {' · '}
                  {t(`valueChainMap.monitoring.results.climateTheme.${selectedFinding.theme}`, {
                    defaultValue: selectedFinding.theme,
                  })}
                  {' · '}
                  {t(`valueChainMap.monitoring.results.riskSeverity.${selectedFinding.severity}`)}
                  {selectedFinding.horizon ? ` · ${selectedFinding.horizon}` : ''}
                </div>

                {detailView === 'summary' ? (
                  <div className={styles.summaryCard}>
                    <div className={styles.summaryField}>
                      <span className={styles.summaryLabel}>
                        {t('valueChainMap.monitoring.results.climateModalObserved')}
                      </span>
                      <span className={styles.summaryValue}>
                        {dayjs(selectedFinding.observed_at).isValid()
                          ? dayjs(selectedFinding.observed_at).format('MMM D, YYYY')
                          : selectedFinding.observed_at}
                      </span>
                    </div>
                    <div className={styles.summaryField}>
                      <span className={styles.summaryLabel}>
                        {t('valueChainMap.monitoring.results.climateShortageRisk')}
                      </span>
                      <span className={styles.summaryValue}>
                        {selectedFinding.shortage_risk?.summary || '—'}
                      </span>
                    </div>
                    <div className={styles.summaryField}>
                      <span className={styles.summaryLabel}>
                        {t('valueChainMap.monitoring.results.climateRecommendedAction')}
                      </span>
                      <span className={styles.summaryValue}>
                        {selectedFinding.recommended_action || '—'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className={styles.diagramTrail}>
                    {(selectedFinding.impact_trail || []).map((node, nodeIndex) => (
                      <div
                        key={`${selectedFinding.id}-${node.role}-${node.entity_name}`}
                        className={clsx(styles.trailStep, {
                          [styles.trailStepOrigin]: nodeIndex === 0,
                        })}
                      >
                        <div className={styles.trailCard}>
                          <div className={styles.trailRole}>{node.role}</div>
                          <div className={styles.trailName}>{node.entity_name}</div>
                        </div>
                        {nodeIndex < selectedFinding.impact_trail!.length - 1 ? (
                          <span aria-hidden="true">→</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className={styles.emptyDetail}>
                {t('valueChainMap.monitoring.results.climateModalSelectIssue')}
              </p>
            )}

            {canCreateImpact ? (
              <div className={styles.impactBridgeSection}>
                <p className={styles.impactBridgeDescription}>
                  {t('valueChainMap.monitoring.climate.impactBridgeCtaFromEntityDescription')}
                </p>
                <button
                  type="button"
                  className={styles.impactBridgeButton}
                  disabled={isImpactScenarioCreating}
                  onClick={() => onCreateImpactScenario?.(
                    selectedEntity ? [selectedEntity.unitId] : undefined,
                  )}
                >
                  {t('valueChainMap.monitoring.climate.impactBridgeCtaFromEntity')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Backdrop>
  );

  return createPortal(modal, document.body);
}

export default ClimateAnalysisModal;
